import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { inventoryApi, ordersApi } from '../api';
import toast from 'react-hot-toast';
import QRCode from 'react-qr-code';

export default function InventoryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inventory, setInventory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    fetchInventory();
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchOrdersByInventory();
    }
  }, [id]);

  const fetchInventory = async () => {
    try {
      const res = await inventoryApi.getById(id);
      console.log('API Response:', res);
      console.log('res.data:', res?.data);
      // Handle both response formats: { data: inventory } and { data: { inventory } }
      if (res?.data?.data?.inventory) {
        setInventory(res.data.data.inventory);
      } else if (res?.data?.data) {
        setInventory(res.data.data);
      } else if (res?.data) {
        setInventory(res.data);
      }
    } catch (err) {
      console.error('Error fetching inventory:', err);
      toast.error(err.response?.data?.message || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrdersByInventory = async () => {
    try {
      const res = await ordersApi.getByInventoryId(id);
      if (res?.data?.data?.orders) {
        setOrders(res.data.data.orders);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStockStatus = (inventory) => {
    if (!inventory) return 'unknown';
    const isLowStock = Number(inventory.quantityAvailable) <= Number(inventory.lowStockThreshold);
    return isLowStock ? 'low_stock' : 'in_stock';
  };

  const getStockStatusLabel = (status) => {
    switch (status) {
      case 'low_stock':
        return 'Low Stock';
      case 'in_stock':
        return 'In Stock';
      default:
        return 'Unknown';
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-screen">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!inventory) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <p>Inventory not found</p>
          <Link to="/inventory" className="btn btn-primary">Back to Inventory</Link>
        </div>
      </div>
    );
  }

  const stockStatus = getStockStatus(inventory);

  const printQR = () => {
    const qrValue = `${window.location.origin}/inventory/${inventory.id}`;
    const win = window.open('', '_blank', 'width=400,height=500');

    win.document.write(`
    <html>
      <head>
        <title>Print QR Label - ${inventory.lot.lotNumber}</title>
        <style>
          body{
            display:flex;
            justify-content:center;
            align-items:center;
            height:100vh;
            font-family:Arial;
            margin:0;
            padding:20px;
          }

          #label{
            text-align:center;
            padding:20px;
            border:2px solid #000;
            border-radius:8px;
          }

          #qr-img{
            width:120px;
            height:120px;
          }

          .lot-number{
            font-size:16px;
            font-weight:bold;
            margin-top:10px;
          }

          .cloth-type{
            font-size:12px;
            color:#666;
          }

          @media print {
            body { padding: 0; }
          }
        </style>
      </head>

      <body>
        <div id="label">
          <img id="qr-img" src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrValue)}" alt="QR Code" />
          <div class="lot-number">${inventory.lot.lotNumber}</div>
          <div class="cloth-type">${inventory.clothType?.replace('_', ' ') || ''}</div>
        </div>

        <script>
          window.onload = function(){
            window.print();
            window.onafterprint = function(){
              window.close();
            }
          }
        </script>
      </body>
    </html>
  `);

    win.document.close();
  };
  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4">
          <Link to="/inventory" className="btn btn-secondary btn-sm">← Back</Link>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>
              {inventory.lot?.lotNumber || 'Inventory'} - {inventory.clothType?.replace('_', ' ')}
            </h1>
            <span className={`badge badge-${stockStatus === 'low_stock' ? 'cancelled' : 'ready'}`}>
              {getStockStatusLabel(stockStatus)}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/inventory?adjust=${inventory.id}`)}
          >
            Adjust Stock
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Inventory Info Card */}
          <div className="card p-2">
            <h3 className="card-title">Inventory Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Cloth Type</span>
                <span className="info-value font-semibold">{inventory.clothType?.replace('_', ' ')}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Stock Status</span>
                <span className={`info-value badge badge-${stockStatus === 'low_stock' ? 'cancelled' : 'ready'}`}>
                  {getStockStatusLabel(stockStatus)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Available Quantity</span>
                <span className="info-value font-semibold" style={{ color: stockStatus === 'low_stock' ? 'var(--danger)' : 'var(--success)' }}>
                  {Number(inventory.quantityAvailable).toFixed(2)}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Reserved Quantity</span>
                <span className="info-value">{Number(inventory.quantityReserved || 0).toFixed(2)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Effective Stock</span>
                <span className="info-value font-semibold">{Number(inventory.effectiveStock || 0).toFixed(2)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Low Stock Threshold</span>
                <span className="info-value">{inventory.lowStockThreshold}</span>
              </div>
            </div>
          </div>

          {/* Lot Details Card */}
          {inventory.lot && (
            <div className="card p-2">
              <h3 className="card-title">Lot Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Lot Number</span>
                  <span className="info-value mono font-semibold">{inventory.lot.lotNumber}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Fabric Type</span>
                  <span className="info-value">{inventory.lot.fabricType}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Color</span>
                  <span className="info-value">{inventory.lot.color}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Price Per Unit</span>
                  <span className="info-value">₹{Number(inventory.lot.purchasePricePerUnit).toLocaleString('en-IN')}/{inventory.lot.unit}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Supplier</span>
                  <span className="info-value">{inventory.lot.supplier || '—'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Purchase Date</span>
                  <span className="info-value">{formatDate(inventory.lot.purchasedAt)}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            {/* Orders Card - Shows orders for this lot */}
            <div className="card p-2">
              <h3 className="card-title">Orders for this Lot</h3>
              {ordersLoading ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Loading orders...
                </div>
              ) : orders.length > 0 ? (
                <div className="orders-list">
                  <div className="orders-summary">
                    <span className="orders-count">{orders.length}</span>
                    <span className="orders-label">Total Orders</span>
                  </div>
                  <div className="customer-orders">
                    {orders.map((order) => (
                      <div key={order.orderId} className="customer-order-item">
                        <div className="customer-info">
                          <span className="customer-name">{order.customer?.name || 'Unknown'}</span>
                          <span className="order-number">{order.orderNumber}</span>
                        </div>
                        <div className="cloth-info">
                          <span className="cloth-quantity">{Number(order.totalClothUsed).toFixed(2)} mtr</span>
                          <span className={`badge badge-${order.status === 'delivered' ? 'delivered' : order.status === 'cancelled' ? 'cancelled' : order.status === 'ready' ? 'ready' : order.status === 'stitching' ? 'stitching' : 'pending'}`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                  No orders found for this lot
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Quick Stats Card */}
          <div className="card p-2">
            <h3 className="card-title">Quick Information</h3>
            <div className="summary-table">
              <div className="summary-row">
                <span>Inventory ID</span>
                <span className="mono text-muted">#{inventory.id}</span>
              </div>
              <div className="summary-row">
                <span>Lot ID</span>
                <span className="mono text-muted">#{inventory.lotId}</span>
              </div>
              <div className="summary-row">
                <span>Stock Status</span>
                <span className={`badge badge-${stockStatus === 'low_stock' ? 'cancelled' : 'ready'}`}>
                  {getStockStatusLabel(stockStatus)}
                </span>
              </div>
              <div className="summary-row">
                <span>Low Stock Alert</span>
                <span>{stockStatus === 'low_stock' ? '⚠️ Yes' : '✓ No'}</span>
              </div>
              <div className="summary-row total">
                <span>Total Value</span>
                <span className="font-bold">
                  ₹{(
                    Number(inventory.quantityAvailable) * Number(inventory.lot?.purchasePricePerUnit || 0)
                  ).toLocaleString('en-IN')}
                </span>
              </div>
            </div>
          </div>

          {/* QR Code Card */}
          {inventory.lot?.lotNumber && (
            <div className="card p-2">
              <h3 className="card-title">QR Code</h3>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '16px 0' }}>

                <div id="qr-print">
                  <QRCode
                    value={`${window.location.origin}/inventory/${inventory.id}`}
                    size={150}
                    style={{ padding: '8px', background: '#fff', borderRadius: '8px' }}
                  />

                  <p style={{ textAlign: 'center', marginTop: '6px', fontWeight: 'bold' }}>
                    {inventory.lot.lotNumber}
                  </p>

                  <p style={{ textAlign: 'center', fontSize: '12px' }}>
                    {inventory.clothType?.replace('_', ' ')}
                  </p>
                </div>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={printQR}
                >
                  🖨 Print QR Label
                </button>

              </div>
            </div>
          )}

          {/* Actions Card */}
          <div className="card p-2">
            <h3 className="card-title">Actions</h3>
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/inventory?adjust=${inventory.id}`)}
              >
                📊 Adjust Stock
              </button>
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/inventory?history=${inventory.id}`)}
              >
                📜 View History
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .grid-2 {
          display: grid;
        }
        @media (max-width: 1024px) {
          .grid-2 {
            grid-template-columns: 1fr !important;
          }
        }
        .flex {
          display: flex;
        }
        .flex-col {
          flex-direction: column;
        }
        .items-center {
          align-items: center;
        }
        .gap-2 {
          gap: 8px;
        }
        .gap-4 {
          gap: 16px;
        }
        .card-title {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 16px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }
        .info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
        }
        .info-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .info-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .info-value {
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .summary-table {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 0.9rem;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
        }
        .summary-row:last-child {
          border-bottom: none;
        }
        .summary-row.total {
          font-size: 1rem;
          padding-top: 12px;
          margin-top: 4px;
          border-top: 2px solid var(--border-color);
        }
        .badge-active { background: #d1fae5; color: #065f46; }
        .badge-inactive { background: #fee2e2; color: #991b1b; }
        .badge-pending { background: #fef3c7; color: #92400e; }
        .badge-stitching { background: #dbeafe; color: #1e40af; }
        .badge-qc { background: #e0e7ff; color: #3730a3; }
        .badge-ready { background: #d1fae5; color: #065f46; }
        .badge-delivered { background: #dcfce7; color: #166534; }
        .badge-cancelled { background: #fee2e2; color: #991b1b; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        .badge-done { background: #d1fae5; color: #065f46; }
        .badge-in_progress { background: #dbeafe; color: #1e40af; }
        
        /* Orders List Styles */
        .orders-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .orders-summary {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: var(--bg-secondary);
          border-radius: 8px;
        }
        .orders-count {
          font-size: 1.5rem;
          font-weight: bold;
          color: var(--primary);
        }
        .orders-label {
          font-size: 0.9rem;
          color: var(--text-muted);
        }
        .customer-orders {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .customer-order-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 12px;
          background: var(--bg-secondary);
          border-radius: 6px;
          border-left: 3px solid var(--primary);
        }
        .customer-info {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .customer-name {
          font-weight: 600;
          font-size: 0.9rem;
          color: var(--text-primary);
        }
        .order-number {
          font-size: 0.75rem;
          color: var(--text-muted);
          font-family: monospace;
        }
        .cloth-info {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cloth-quantity {
          font-weight: 600;
          color: var(--success);
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
}
